import React from "react";
import { HealthArea, HealthInactiveArea } from "../analyticsApi";
import { formatDecimal, healthBand, inputUnit } from "../analyticsUtils";
import { CARD } from "./ui";

const BAND_TEXT = {
  green: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  red: "text-rose-600 dark:text-rose-400",
};

const BAND_BAR = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500" };

function formatInputValue(value: number | null, unit: string): string {
  if (value == null) return "—";
  return unit === "%" ? `${formatDecimal(value)}%` : `${formatDecimal(value)} ${unit}`;
}

export function ActiveAreaCard({ area }: { area: HealthArea }) {
  const band = healthBand(area.score);
  const liftedInput = area.whatIf ? area.inputs.find((i) => i.key === area.whatIf?.inputKey) : undefined;
  return (
    <div className={`${CARD} p-4 flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{area.label}</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">Weight {area.weight}</p>
        </div>
        <span className={`text-2xl font-bold tabular-nums ${band ? BAND_TEXT[band] : ""}`}>{formatDecimal(area.score)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div className={`h-full rounded-full ${band ? BAND_BAR[band] : "bg-slate-400"}`} style={{ width: `${Math.max(0, Math.min(100, area.score))}%` }} />
      </div>
      <ul className="space-y-1.5">
        {area.inputs.map((input) => {
          const unit = inputUnit(input.key);
          return (
            <li key={input.key} className="flex items-baseline justify-between gap-3 text-xs">
              <span className="text-slate-500 dark:text-slate-400">{input.label}</span>
              <span className="text-slate-800 dark:text-slate-100 font-medium tabular-nums whitespace-nowrap">
                {formatInputValue(input.value, unit)}
                {input.score != null && <span className="text-slate-400 dark:text-slate-500 font-normal"> · {formatDecimal(input.score)} pts</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {area.whatIf && liftedInput && (
        <p className="text-[11px] leading-relaxed rounded-lg px-2.5 py-2 bg-slate-50 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300">
          Lift <span className="font-medium">{liftedInput.label}</span> to the good band → area{" "}
          <span className="font-semibold">{formatDecimal(area.whatIf.areaScoreIfGood)}</span>, overall{" "}
          <span className="font-semibold">{formatDecimal(area.whatIf.scoreIfGood)}</span>
        </p>
      )}
    </div>
  );
}

export function InactiveAreaCard({ area }: { area: HealthInactiveArea }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-4 bg-slate-50/60 dark:bg-slate-800/40">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{area.label}</p>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">Not scored</span>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">{area.reason}</p>
    </div>
  );
}
